import { Box, Button, Sheet, Stack, Typography } from "@mui/joy";
import { useEffect, useState } from "react";
import { theme } from "../../components/Theme";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InventoryService } from "../../services/inventory.service";
import { CatalogType } from "../../types/catalog";
import { getActiveBusiness, useAuthStore } from "../../store/session";
import { CartDataType } from "../../types/cart";
import { CatalogInventoryItemType } from "../../types/item";
import { OrderService } from "../../services/order.service";
import {
  DISCOUNT_AMOUNT_OPTIONS_ENUM,
  OrderStatusEnum,
  PaymentFlowTypeEnum,
} from "../../types/order";
import { BusinessService } from "../../services/business.service";
import toast from "react-hot-toast";
import showErrorToast, { ErrorToastConfig } from "../../components/Error";
import { IExternalBusiness } from "../../types/business";
import {
  calculatePrice,
  getConversionRate,
  trackCart,
  updateCartQty,
} from "../../utils/cart";
import { FormProvider, useForm } from "react-hook-form";
import { PaymentService, PaymentType } from "../../services/payment.service";
import LoadingModal from "../../components/LoadingModal/LoadingModal";
import PaymentInfoModal from "../../components/payments/PaymentInfoModal";
import { IAddressResponse } from "../../types/profile";
import PaymentSuccessModal from "./components/PaymentSuccessModal";
import OrderSummary from "./components/Form/OrderSummary";
import FinalizeOrderSection from "./components/Form/FinalizeOrderSection";
import useFormContainer from "./useFormContainer";
import { useFeeStore } from "./components/Form/store";
import NotFound from "../NotFound/NotFound";
import { BNPLPaymentModal } from "../../components/payments/BNPLPaymentModal";
import PaymentOptions from "../../components/payments/PaymentOptions";
import { getCountryInfo } from "../../utils/country";
import { OfflinePaymentModal } from "../../components/payments/OfflinePaymentModal";

const CartCheckout = () => {
  const { t } = useTranslation();

  const { catalogId } = useParams();

  const { session } = useAuthStore();

  const [catalog, setCatalog] = useState<CatalogType>();
  const [checkOutItems, setCheckOutItems] = useState<CartDataType>();
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState<{
    show: boolean;
    isCompleted: boolean;
  }>({ show: false, isCompleted: false });
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);
  const [showPaymentOfflineModal, setShowPaymentOfflineModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [paymentTransactionDetails, setPaymentTransactionDetails] =
    useState<any>(null);
  const [_orderId, setOrderId] = useState<string>();
  const [items, setItems] = useState<CatalogInventoryItemType[]>([]);
  const [bnplPayment, setBNPLPayment] = useState(false);
  const [paymentAttempts, setPaymentAttempts] = useState(0);

  const methods = useForm({
    defaultValues: {
      phoneNumber: "99911112222",
      phoneNumberCountryCode: "62",
      fullName: "Chris",
      companyCountryCode: "ID",
      paymentMethod: "",
      address: {
        id: "addr_1JkLmN9oP7qR5sT3vX",
        updatedAt: "2023-11-15T10:30:45.000Z",
        createdAt: "2023-11-10T08:15:22.000Z",
        deletedAt: "",
        title: "Home",
        fullName: "John Doe",
        countryCode: "US",
        phoneNumberCountryCode: "1",
        phoneNumber: "5551234567",
        address: "123 Main Street",
        address2: "Apt 4B",
        province: "California",
        postalCode: "90210",
        defaultAddress: true,
        account: "acc_9pXqR2sT4vW6yZ8bC",
      },
      status: "",
      remarks: "",
    },
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { isValid },
  } = methods;

  const { isLoading, setIsLoading, handleValidateStep } = useFormContainer(
    methods,
    catalog
  );

  const {
    paymentFee,
    deliveryFee,
    taxFee,
    subtotal,
    subtotalMultiPayment,
    setPaymentFee,
    discount,
    discountPercentage,
  } = useFeeStore();

  /**
   * fetch catalog based on catalogId
   */
  useEffect(() => {
    if (catalogId) {
      setIsLoading(true);
      InventoryService.getOneCatalog({ catalogId: catalogId })
        .then((res) => {
          setCatalog(res);
          setCheckOutItems(
            JSON.parse(sessionStorage.getItem("checkOutItems") || "{}")
          );
        })
        .catch((err) => console.error(err))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [catalogId]);

  const onSubmit = (data: any) => {
    console.log("SUBMITTED DATA", data);

    if (!paymentTransactionDetails) {
      const paymentMethod = getValues("paymentMethod");
      if (paymentMethod === "offline-payment") {
        setPaymentAttempts((prev) => prev + 1);
        setShowPaymentOfflineModal(true);
        return;
      } else if (paymentMethod === "linkz-bnpl") {
        setPaymentAttempts((prev) => prev + 1);
        setBNPLPayment(true);
        return;
      }
    }

    const selectedIds = items.map((i) => i.inventoryItem.id);
    handleCreateOrder(selectedIds, items);
  };

  const getPaymentStatus = ({
    orderId,
    onSuccess,
  }: {
    orderId: string;
    onSuccess?: () => void;
  }) => {
    let paymentType = PaymentType.FINAL_PAYMENT;
    if (catalog?.paymentFlowType === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS) {
      paymentType = "1" as any;
    }
    PaymentService.enquiryPaymentStatus(
      orderId,
      catalog?.business.id || "",
      `${paymentType}` as any
    ).then((response) => {
      setPaymentInfo({
        ...response.data.transactionInfo,
        updatedAt: response.data.updatedAt,
      });
      setPaymentTransactionDetails(response.data);
      onSuccess?.();
    });
  };

  const handlePaymentContinue = async (orderId: string, amount: number) => {
    if (!catalog) return;

    const payload = getValues();
    const method = payload.paymentMethod?.split(":")[0];
    const option = payload.paymentMethod?.split(":")[1];
    let paymentType = PaymentType.FINAL_PAYMENT;
    let orderStatus = OrderStatusEnum.CONFIRMED;

    if (catalog.paymentFlowType === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS) {
      paymentType = "1" as any;
      orderStatus = OrderStatusEnum.PENDING_FINAL_PAYMENT;
    } else if (
      catalog.paymentFlowType === PaymentFlowTypeEnum.FULL_AFTER_DELIVERY
    ) {
      orderStatus = OrderStatusEnum.PROCESSING;
    }

    await OrderService.updateOrderStatus(orderId, orderStatus);

    if (catalog.paymentFlowType === PaymentFlowTypeEnum.FULL_AFTER_DELIVERY) {
      sessionStorage.removeItem("checkoutOrderId");
      setIsLoading(false);
      window.location.href = `/client/orders/${orderId}`;
      return;
    }

    if (method) {
      console.log("Payable Amount", amount, getValues("status"));
      await PaymentService.requestPayment(
        PaymentService.createPaymentRequestPayload({
          method,
          option: option ?? "",
          transactionType: "order",
          transactionId: orderId,
          amount: amount + paymentFee,
          currency: catalog.currency,
          redirectUrl: window.location.origin + `/client/orders/${orderId}`,
          paymentType: `${paymentType}` as any,
        }),
        catalog.business.id
      )
        .then((response: any) => {
          if (response) {
            if (getValues("paymentMethod") === "linkz-bnpl") {
              setShowPaymentSuccessModal({
                show: true,
                isCompleted: true,
              });
              return;
            }

            if (getValues("paymentMethod") !== "offline-payment") {
              getPaymentStatus({
                orderId,
                onSuccess: () => {
                  setPaymentAttempts((prev) => prev + 1);
                  setShowPaymentInfoModal(true);
                },
              });
            } else {
              sessionStorage.removeItem("checkoutOrderId");
              window.location.href = `/client/orders/${orderId}`;
            }
          }
        })
        .catch((err: any) => {
          console.log("ERROR REQUESTING PAYMENT", err);
          toast(showErrorToast(err), ErrorToastConfig);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleCreateOrder = (
    selectedIds: string[],
    items: CatalogInventoryItemType[]
  ) => {
    setIsLoading(true);
    const orderExist = sessionStorage.getItem("checkoutOrderId");
    if (orderExist) {
      if (paymentTransactionDetails) {
        setIsLoading(false);
        setShowPaymentInfoModal(true);
        return;
      }

      let amount =
        parseFloat(subtotal.toString()) +
        parseFloat(deliveryFee.toString()) +
        parseFloat(taxFee.toString()) -
        parseFloat(discount.toString());
      if (catalog?.paymentFlowType === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS) {
        amount = parseFloat(subtotalMultiPayment.toString());
      }
      handlePaymentContinue(orderExist, amount);
      return;
    }

    if (catalog && !orderExist) {
      BusinessService.getMyBusinessRoles(getActiveBusiness()?.id || "").then(
        (_res) => {
          const sellerBusiness: IExternalBusiness = catalog.business;

          if (sellerBusiness) {
            // const sellerAccount = sellerBusiness?.role?.[0]?.account?.id ?? "";
            const roles = sellerBusiness.role as any[];
            const sellerAccount: any =
              sellerBusiness.role.find((r: any) => r.isOwner) ?? roles[0];

            if (sellerAccount) {
              OrderService.initiateDraft({
                grandTotalAmount: 0,
                paymentTerm: "",
                sellerId: catalog?.business.id || "",
                buyerId: getActiveBusiness()?.id || "",
                invoiceNumber: "",
                createdById: session?.account.id || null,
                status: OrderStatusEnum.DRAFT,
                isFromCatalog: true,
                isFromCatalogPrivate: catalog.catalogType === "PRIVATE",
                isUnPaidCatalogueOrder: true,
              })
                .then((res) => {
                  if (res.data.id) {
                    const selectedItems = items.filter((i) =>
                      selectedIds.includes(i.inventoryItem.id)
                    );
                    const itemsToBeOrdered = selectedItems.map((i) => {
                      const cartItem = checkOutItems?.items.find(
                        (item) => item.cid === i.id
                      );
                      return {
                        productId: i.inventoryItem.id,
                        sku: i.inventoryItem.sku,
                        productName: i.inventoryItem.productName,
                        quantity: cartItem?.quantity ?? 1,
                        uom: i.inventoryItem[cartItem?.uom ?? "uom1"],
                        conversion: getConversionRate(
                          cartItem?.uom ?? "uom1",
                          i.inventoryItem
                        ),
                        price: calculatePrice(i, cartItem?.uom ?? "uom1"),
                        discount: 0,
                        totalPrice:
                          calculatePrice(i, cartItem?.uom ?? "uom1") *
                          (cartItem?.quantity ?? 1),
                        businessId: i.inventoryItem.business,
                        inventoryType: i.inventoryItem.inventoryType,
                        currency: catalog.currency ?? i.currency,
                        productDescription: i.inventoryItem.productDescription,
                        createdById: i.createdBy || "",
                      };
                    });

                    let orderUpdatePayload = {
                      currency:
                        items[0].currency ?? items[0].inventoryItem.currency,
                      orderItems: itemsToBeOrdered,
                      grandTotalAmount:
                        subtotal + taxFee + deliveryFee - discount,
                      paymentFlowType: catalog.paymentFlowType,
                      status: OrderStatusEnum.AWAITING_RESPONSE,
                      paymentMethod: getValues("paymentMethod"),
                      createdById: session?.account.id || "",
                      sellerIds: {
                        businessId: catalog?.business.id,
                        accountId: sellerAccount.account,
                      },
                      buyerIds: {
                        businessId: getActiveBusiness()?.id || "",
                        accountId: session?.account.id || "",
                      },
                      deliveryFee,
                      totalTax: taxFee,
                      additionalTaxLabel: catalog?.taxId?.taxLabel ?? undefined,
                      additionalTax: catalog?.taxId?.taxAmount,
                      additionalTaxType: catalog?.taxId?.taxType,
                      additionalTaxAmountType: catalog?.taxId?.taxAmountType,
                      additionalDiscount:
                        catalog.discountFeeConfig?.amountType ===
                        DISCOUNT_AMOUNT_OPTIONS_ENUM.AMOUNT
                          ? discount
                          : discountPercentage,
                      additionalDiscountType:
                        catalog.discountFeeConfig?.amountType ?? "PERCENTAGE",
                      orderShippingAddress: getValues(
                        "address"
                      ) as IAddressResponse,
                      remarks: getValues("remarks"),
                    };

                    if (
                      catalog.paymentFlowType ===
                        PaymentFlowTypeEnum.MULTIPLE_PAYMENTS &&
                      catalog.multiPaymentsData
                    ) {
                      const multiPaymentsData = {
                        paymentTimes: catalog.multiPaymentsData.paymentTimes,
                        paymentTypes: catalog.multiPaymentsData.paymentTypes,
                        paymentHistory:
                          catalog.multiPaymentsData.paymentHistory.map((e) => {
                            return {
                              time: e.time,
                              percentage: e.percentage,
                              status: "PENDING",
                              amount:
                                ((parseFloat(subtotal.toString()) +
                                  parseFloat(taxFee.toString()) +
                                  parseFloat(deliveryFee.toString()) -
                                  parseFloat(discount.toString())) *
                                  parseFloat(e.percentage.toString())) /
                                100,
                            };
                          }),
                      };
                      orderUpdatePayload = {
                        ...orderUpdatePayload,
                        multiPaymentsData,
                      } as any;
                    }

                    if (catalog.currency) {
                      orderUpdatePayload.currency = catalog.currency;
                    }

                    OrderService.update(res.data.id, orderUpdatePayload)
                      .then((res) => {
                        if (checkOutItems) {
                          trackCart({
                            cartItems: checkOutItems,
                            catalog,
                            timing: "post",
                            items,
                          });
                        }

                        items.forEach((i) => {
                          const checkOutItem = checkOutItems?.items.find(
                            (ci) => ci.cid === i.id
                          );
                          updateCartQty({
                            catalogId: catalog.id,
                            accountId: session?.account.id || "wildcart",
                            override: false,
                            quantityChange: -(checkOutItem?.quantity || 0),
                            inventoryItem: i,
                            direct: session?.account.id ? false : true,
                            activeUOM: checkOutItem?.uom || "uom1",
                            storageType: session?.account.id
                              ? "local"
                              : "session",
                          });
                        });

                        if (res.id) {
                          setOrderId(res.id);
                          sessionStorage.setItem("checkoutOrderId", res.id);

                          let amount =
                            parseFloat(subtotal.toString()) +
                            parseFloat(deliveryFee.toString()) +
                            parseFloat(taxFee.toString()) -
                            parseFloat(discount.toString());

                          if (
                            catalog.paymentFlowType ===
                            PaymentFlowTypeEnum.MULTIPLE_PAYMENTS
                          ) {
                            amount = parseFloat(
                              subtotalMultiPayment.toString()
                            );
                          }

                          handlePaymentContinue(res.id, amount);
                        }
                      })
                      .catch((error) => {
                        throw new Error(error);
                      });
                  }
                })
                .catch((error) => console.error(error));
            }
          } else {
            setIsLoading(false);
            toast(t("Error: Seller Not Found", ErrorToastConfig));
          }
        }
      );
    }
  };

  if (!catalogId) {
    return <NotFound />;
  }

  return (
    <Sheet
      sx={{
        width: "100%",
        mt: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "32px",
      }}
    >
      {/* form */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction={"column"} gap={"32px"}>
            {catalog && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                sx={{
                  alignItems: "flex-start",
                  gap: "32px",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  {/* finalize order */}
                  <FinalizeOrderSection catalog={catalog} />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    p: "16px",
                    border: `1px solid ${theme.palette.neutral[300]}`,
                    borderRadius: "8px",
                  }}
                >
                  {checkOutItems && (
                    <OrderSummary
                      catalog={catalog}
                      checkOutItems={checkOutItems}
                      items={items}
                      setItems={setItems}
                    />
                  )}
                </Box>
              </Stack>
            )}

            <Stack
              direction={"column"}
              sx={{
                display: { xs: "flex", md: "none" },
                gap: "24px",
              }}
            >
              {/* payment section */}
              <Stack
                direction={"column"}
                sx={{
                  gap: "16px",
                  p: "16px",
                  border: `1px solid ${theme.palette.neutral[300]}`,
                  borderRadius: "8px",
                }}
              >
                <Typography
                  level="title-md"
                  textColor={theme.palette.primary[500]}
                >
                  {t("catalog.checkout.steps.3.2")}
                </Typography>

                <Typography
                  level="text-sm-medium"
                  textColor={theme.palette.neutral[600]}
                >
                  {t("catalog.checkout.steps.3.2.subtitle")}
                </Typography>

                {/* payment options section */}
                {catalog && (
                  <PaymentOptions
                    control={control}
                    setValue={setValue}
                    onlinePaymentOnly={catalog.catalogType === "PUBLIC"}
                    paymentAmount={
                      catalog.paymentFlowType ===
                      PaymentFlowTypeEnum.MULTIPLE_PAYMENTS
                        ? parseFloat(subtotalMultiPayment.toString())
                        : parseFloat(subtotal.toString()) +
                          parseFloat(deliveryFee.toString()) +
                          parseFloat(taxFee.toString()) -
                          parseFloat(discount.toString())
                    }
                    paymentCurrency={catalog?.currency}
                    handleChangePaymentFee={setPaymentFee}
                    applicableAdminFee={catalog?.business.applicableAdminFee}
                    sellerId={catalog?.business.id}
                    sellerCurrency={
                      getCountryInfo(
                        catalog?.business.companyCountry ?? "ID",
                        "LOCALE"
                      )?.currency
                    }
                  />
                )}
              </Stack>

              <Button
                id="public-checkout-paynow-btn"
                fullWidth
                sx={{
                  maxWidth: { xs: "100%", lg: "100px" },
                  ml: "auto",
                }}
                disabled={
                  !isValid || !handleValidateStep(3) || !getValues("address")
                }
                type={"submit"}
              >
                {t("catalog.checkout.paynowBtn")}
              </Button>
            </Stack>
          </Stack>
        </form>
      </FormProvider>

      {/* modals */}
      <LoadingModal isLoading={isLoading} />

      <BNPLPaymentModal
        paymentAttempts={paymentAttempts}
        open={bnplPayment}
        setOpenModal={(open: boolean) => {
          setBNPLPayment(open);
        }}
        onFinish={() => {
          const selectedIds = items.map((i) => i.inventoryItem.id);
          handleCreateOrder(selectedIds, items);
        }}
        onChangePaymentMethod={() => {
          setPaymentInfo(null);
          setPaymentTransactionDetails(null);
        }}
      />

      {catalog && paymentTransactionDetails && paymentInfo && (
        <PaymentInfoModal
          open={showPaymentInfoModal}
          paymentAttempts={paymentAttempts}
          setOpenModal={setShowPaymentInfoModal}
          paymentInfo={paymentInfo}
          status={paymentTransactionDetails?.status}
          onFinish={() => {
            setShowPaymentSuccessModal({
              show: true,
              isCompleted: false,
            });
          }}
          onChangePaymentMethod={() => {
            setPaymentInfo(null);
            setPaymentTransactionDetails(null);
          }}
          currency={catalog.currency}
        />
      )}

      <PaymentSuccessModal
        show={showPaymentSuccessModal.show}
        isCompleted={showPaymentSuccessModal.isCompleted}
        onClose={() => {
          sessionStorage.removeItem("checkoutOrderId");
          window.location.href = `/client/suppliers/${catalogId}/items`;
        }}
        onConfirm={() => {
          const orderExist = sessionStorage.getItem("checkoutOrderId");
          sessionStorage.removeItem("checkoutOrderId");
          window.location.href = `/client/orders/${orderExist}`;
        }}
      />

      <OfflinePaymentModal
        open={showPaymentOfflineModal}
        paymentAttempts={paymentAttempts}
        setOpenModal={(open: boolean) => {
          setShowPaymentOfflineModal(open);
        }}
        onChangePaymentMethod={() => {
          setPaymentInfo(null);
          setPaymentTransactionDetails(null);
        }}
        onFinish={() => {
          const selectedIds = items.map((i) => i.inventoryItem.id);
          handleCreateOrder(selectedIds, items);
        }}
      />
    </Sheet>
  );
};

// const CartItemHeader = ({ catalog }: { catalog: CatalogType }) => {
//   const { t } = useTranslation();

//   return (
//     <ListItem
//       key={0}
//       sx={{
//         display: "block",
//         borderBottom: "1px solid #F2F4F7",
//         pb: "16px",
//       }}
//     >
//       <div className={`pt-4 flex align-middle w-full gap-4`}>
//         <div
//           className={`flex flex-col lg:flex-row lg:align-middle w-full gap-4`}
//         >
//           <Stack
//             className="gap-4 w-full lg:w-[37%]"
//             direction={"row"}
//             alignItems={"center"}
//           >
//             <img
//               src={"/inventory-img-placeholder.png"}
//               alt="item_image"
//               className="lg:border border-gray-300 rounded-t-lg lg:rounded-lg w-[32px] h-[32px] object-cover flex-shrink-0"
//             />
//             <Stack>
//               <Typography fontSize={15} fontWeight={500} textColor={"#000"}>
//                 {catalog.catalogName}
//               </Typography>
//               <If
//                 condition={
//                   (catalog.business as any).kycPaymentGatewayStatus ===
//                   "VALIDATED"
//                 }
//               >
//                 <Typography fontSize={13} fontWeight={400} textColor={"#000"}>
//                   {t("catalog.supplier.items.verifiedSupplier")}
//                 </Typography>
//               </If>
//             </Stack>
//           </Stack>

//           <div className="lg:flex w-[63%] justify-between items-center gap-4 hidden">
//             <div className="w-[30%] text-center">
//               <Typography fontSize={16} fontWeight={500} textColor={"#000"}>
//                 {t("catalog.checkout.unitPrice")}
//               </Typography>
//             </div>
//             <div className="hidden lg:block">
//               <Typography fontSize={16} fontWeight={500} textColor={"#000"}>
//                 {t("catalog.checkout.quantity")}
//               </Typography>
//             </div>
//             <div className="w-[30%] text-center">
//               <Typography fontSize={16} fontWeight={500} textColor={"#000"}>
//                 {t("catalog.checkout.subtotal")}
//               </Typography>
//             </div>
//           </div>
//         </div>
//       </div>
//     </ListItem>
//   );
// };

// const CheckoutItem = ({
//   i,
//   checkOutItems,
// }: {
//   catalog: CatalogType;
//   i: CatalogInventoryItemType;
//   checkOutItems: CartDataType;
// }) => {
//   const currency = i.currency ?? i.inventoryItem.currency;
//   const activeUOM: "uom1" | "uom2" | "uom3" = getUOMFromCart(
//     i.id,
//     checkOutItems
//   );
//   const checkOutItem = checkOutItems.items.find((ci) => ci.cid === i.id);

//   return (
//     <ListItem
//       key={i.id}
//       sx={{
//         display: "block",
//         borderBottom: "1px solid #F2F4F7",
//         pb: "16px",
//       }}
//     >
//       <div className={`pt-4 flex align-middle w-full gap-4`}>
//         <div className={`flex lg:align-middle w-full gap-4 justify-between`}>
//           <Stack
//             className="gap-4"
//             direction={"row"}
//             alignItems={"center"}
//             justifyContent={{ xs: "space-between", lg: "center" }}
//             width={"100%"}
//           >
//             <img
//               src={`${
//                 i.inventoryItem.thumbnail ?? "/inventory-img-placeholder.png"
//               }`}
//               alt="item_image"
//               // width={100}
//               // height={100}
//               className="lg:border border-gray-300 rounded-t-lg lg:rounded-lg w-[64px] h-[64px] lg:w-[100px] lg:h-[100px] object-contain flex-shrink-0"
//             />
//             <Stack gap={{ xs: "0px", lg: "8px" }} width={"100%"}>
//               <Typography
//                 fontSize={{ xs: 14, lg: 16 }}
//                 fontWeight={500}
//                 textColor={"#475467"}
//               >
//                 {i.inventoryItem.sku ?? "-"}
//               </Typography>
//               <div className="line-clamp-1 lg:line-clamp-none">
//                 <Typography
//                   fontSize={{ xs: 14, lg: 16 }}
//                   fontWeight={700}
//                   textColor={"#101828"}
//                 >
//                   {i.inventoryItem.productName}
//                 </Typography>
//               </div>
//               <div className="visible lg:hidden">
//                 <Typography
//                   fontSize={{ xs: 14, lg: 16 }}
//                   fontWeight={500}
//                   textColor={"#475467"}
//                 >
//                   {`${checkOutItem?.quantity} ${
//                     i.inventoryItem[activeUOM] || "unit"
//                   }`}
//                 </Typography>
//               </div>
//             </Stack>
//           </Stack>

//           <div className="flex w-[40%] lg:w-[63%] justify-between items-center gap-4">
//             <div className="w-full lg:w-[30%] lg:text-center text-left">
//               <Typography fontSize={16} fontWeight={500} textColor={"#000"}>
//                 {`${currency} ${formatPrice(
//                   calculatePrice(i, activeUOM),
//                   currency
//                 )}`}
//               </Typography>
//               {/* <span className="line-through text-xs text-neutral-400">
//                 {"IDR 12.000.000,00"}
//               </span> */}
//             </div>
//             <div className="hidden lg:block text-center">
//               <Typography fontSize={16} fontWeight={500} textColor={"#000"}>
//                 {`${checkOutItem?.quantity} ${
//                   i.inventoryItem[activeUOM] || "unit"
//                 }`}
//               </Typography>
//             </div>
//             <div className="w-[30%] text-center hidden lg:block">
//               <Typography fontSize={16} fontWeight={500} textColor={"#000"}>
//                 {`${currency} ${formatPrice(
//                   (checkOutItem?.quantity || 1) * calculatePrice(i, activeUOM),
//                   currency
//                 )}`}
//               </Typography>
//             </div>
//           </div>
//         </div>
//       </div>
//     </ListItem>
//   );
// };

export default CartCheckout;
